<!-- 
追加可能なセレクトボックスを表示する
@props
  min 追加可能な数の下限を指定する
  max 追加可能な数の上限数を指定する
  initial 追加ボタン押下時の初期データを指定する
  appendable 追加できるのならtrue、できないのならfalseを指定する
-->

<template>
  <div>
    <!-- コンテンツのうち、セレクトボックスで操作できないものを出力する -->
    <div v-for="content in fixedContents" :key="content.id">
      <div class="mb-4 mt-4 ml-3">
        {{ content.display }}
      </div>
    </div>
    <!-- セレクトボックスで操作できるものを出力する -->
    <v-input
      v-for="(item, index) in selected"
      :key="item.id"
      dense
      :error-messages="errorIndexList.includes(index) ? ['重複しています'] : []"
    >
      <slot :item="{ item, index, selected }"> </slot>
      <v-btn
        v-if="appendable"
        slot="append"
        icon
        outlined
        color="primary"
        :disabled="
          selected.length <= editableRowMinCount ||
          isDisabled(selected[index].meta)
        "
        @click="remove(index)"
      >
        <v-icon>mdi-minus</v-icon>
      </v-btn>
    </v-input>
    <!-- 追加欄を出力する -->
    <v-input hide-details dense v-if="appendable">
      <div class="ml-3 mt-2 grey--text" v-if="appendNoticeMessage == null">
        選択欄を追加する
      </div>
      <div class="ml-3 mt-2 grey--text" v-else>
        {{ appendNoticeMessage }}
      </div>
      <v-btn
        slot="append"
        icon
        outlined
        color="primary"
        @click="append"
        :disabled="appendNoticeMessage != null"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-input>
  </div>
</template>

<script>
export default {
  name: "append-select-list",
  props: {
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 10,
    },
    initial: {
      type: Object,
      default: null,
    },
    appendable: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    selected: {
      handler() {
        this.onChangedSelectBox(-1);
      },
      deep: true,
    },
  },
  computed: {
    /**
     * セレクトボックスとして並べて表示できる最小数を取得する
     */
    editableRowMinCount() {
      return Math.max(this.min - this.fixedContents.length, 0);
    },
    /**
     * セレクトボックスを追加できるのならtrueを返す
     */
    enableAppend() {
      if (this.initial == null) {
        return false;
      }
      return this.selected.length < this.max;
    },
    /**
     * 追加できない時、追加ボタン横に表示するエラーメッセージを返す
     */
    appendNoticeMessage() {
      if (this.initial == null) {
        return "追加できる項目がありません";
      }
      if (this.selected.length >= this.max) {
        return `${this.max}件を超えて追加することはできません`;
      }
      return null;
    },
  },
  data() {
    let selected = [];
    for (let index = 0; index < this.min; index++) {
      selected.push(this.createInitialData());
    }
    return {
      selected: selected,
      fixedContents: [],
      errorIndexList: [],
    };
  },
  methods: {
    /**
     * セレクトボックスの項目を編集できないのならtrueを返す
     * trueの時、セレクトボックスではなく、固定文字列を表示する
     */
    isDisabled(meta) {
      if (meta && meta.type) {
        return !meta.type.group_editable;
      }
      return false;
    },
    /**
     * セレクトボックス追加時の初期データを作成する
     */
    createInitialData() {
      let data = JSON.parse(JSON.stringify(this.initial));
      data["meta"] = {
        edit: true,
        type: {
          group_editable: true,
        },
      };
      return data;
    },
    /**
     * セレクトボックスを追加する
     */
    append() {
      this.selected.push(this.createInitialData());
    },
    /**
     * セレクトボックスを削除する
     */
    remove(index) {
      if (index < this.selected.length) {
        this.selected.splice(index, 1);
      }
    },
    /**
     * セレクトボックスの重複を検証する
     */
    duplicateCheck(data) {
      return data
        .map((item, index) => {
          if (data.indexOf(item) != index) {
            return index;
          }
          return -1;
        })
        .filter((item) => item >= 0);
    },
    /**
     * セレクトボックスが変更された
     */
    onChangedSelectBox(index) {
      if (index >= 0) {
        if (index < this.selected.length && this.selected[index].meta) {
          this.selected[index].meta.edit = true;
        }
      }
      this.errorIndexList = this.duplicateCheck(
        this.selected.map((item) => {
          if (item.name) {
            return item.name.value;
          }
          if (item.value) {
            return item.value;
          }
          return item;
        })
      );
    },
    /**
     * セレクトボックスと固定文字列で設定したすべてのデータを取得する
     */
    getAllContents() {
      const selected = this.getSelected();
      const fixed = this.getFixedContents();
      let result = [];
      if (selected) {
        for (const item of selected) {
          result.push(item);
        }
      }
      if (fixed) {
        for (const item of fixed) {
          result.push(item);
        }
      }
      return result;
    },
    /**
     * セレクトボックスで取得した全ての可変データを取得する
     */
    getSelected() {
      return this.selected;
    },
    /**
     * セレクトボックスの可変データをシステムから設定する
     */
    setSelected(selected) {
      this.selected = selected;
    },
    /**
     * 固定文字列で出力している固定データを取得する
     */
    getFixedContents() {
      return this.fixedContents.map((item) => {
        return item.data;
      });
    },
    /**
     * 固定文字列で出力している固定データをシステムから設定する
     * data: 元データ
     * display: 表示するデータ
     */
    setFixedContents(contents) {
      this.fixedContents = contents;
    },
    /**
     * 重複などのエラーがあればtrueを返す
     */
    hasError() {
      return this.errorIndexList.length == 0 ? false : true;
    },
  },
};
</script>

<style></style>
